<template>
  <v-card
    v-if="config?.image?.url || config?.title?.text || config?.body?.text"
    elevation="1"
    class="d-flex flex-column align-center full-width pgm-dashboard-container content-container"
    :style="backgroundStyle"
    :class="[backgroundClass, 'rounded-lg']"
    @click="handleButtonClick(true)"
    :ripple="false"
  >
    <img
      v-if="config?.image?.url"
      class="rounded content-image mb-4"
      :class="{ 'content-image--ratio': config.image?.aspectRatio }"
      :src="config.image?.url"
    />
    <div class="d-flex full-width mb-4" v-if="config.title?.text">
      <span :style="titleTextStyle" class="full-width" :class="titleTextClass">{{
        config.title?.text
      }}</span>
    </div>
    <div class="d-flex full-width" v-if="config.body?.text">
      <span
        :style="bodyTextStyle"
        class="full-width body-text ql-editor"
        :class="bodyTextClass"
        v-html="config.body?.text"
      ></span>
    </div>
    <v-btn
      v-if="config?.button"
      :color="config.button?.color || 'brandCyan'"
      class="mt-3"
      :style="buttonTextStyle"
      depressed
      rounded
      @click="handleButtonClick(false)"
    >
      {{ config.button?.text }}
    </v-btn>
  </v-card>
</template>
<script>
export default {
  name: "Content",
  props: {
    config: {
      type: Object,
      required: true,

      // Developer note:
      // This schema is for readability - it doesn't actually restrict anything on the Vue side
      image: {
        type: Object,
        required: false,
        url: {
          type: String,
          required: true,
        },
      },
      background: {
        type: Object,
        required: false,
        color: {
          type: String,
          required: false,
        },
        gradient: {
          type: String,
          required: false,
        },
      },
      title: {
        type: Object,
        required: false,
        text: {
          type: String,
          required: true,
        },
        color: {
          type: String,
          required: false,
          default: "black",
        },
        align: {
          type: String,
          required: false,
          default: "left",
          //   left, center, right
        },
        weight: {
          type: String,
          required: false,
          default: "bold",
          //   bold, normal, 500, 600, 700, 800, 900
        },
      },
      body: {
        type: Object,
        required: false,
        text: {
          type: String,
          required: true,
        },
        color: {
          type: String,
          required: false,
          default: "black",
        },
        align: {
          type: String,
          required: false,
          default: "left",
          //   left, center, right
        },
        weight: {
          type: String,
          required: false,
          default: "bold",
          //   bold, normal, 500, 600, 700, 800, 900
        },
      },
      button: {
        type: Object,
        required: false,
        text: {
          type: String,
          required: true,
        },
        color: {
          type: String,
          required: false,
          default: "brandCyan",
        },
        textColor: {
          type: String,
          required: false,
          default: "black",
        },
      },
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    handleButtonClick(cardClick = false) {
      const url = this.config?.internalUrl || this.config?.externalUrl;
      if (!url || (!!this.config.button && cardClick)) return;

      if (this.config?.internalUrl) this.$router.push(this.config?.internalUrl);
      else window.open(this.config?.externalUrl, "_blank");
    },
  },
  computed: {
    backgroundStyle() {
      let style = {};

      if (this.config?.background?.color) {
        style["background-color"] = this.config?.background?.color;
      } else if (this.config?.background?.gradient) {
        style["background"] = this.config?.background?.gradient;
      }

      return style;
    },
    backgroundClass() {
      let classList = "";
      classList += ` cursor-${
        this.config?.button || (!this.config?.internalUrl && !this.config?.externalUrl)
          ? "default"
          : "pointer"
      }`;

      if (this.isMobile) classList += " pa-2";
      else classList += " pa-4";

      return classList;
    },
    titleTextStyle() {
      let style = {
        color: this.config?.title?.color || "black",
        // "font-weight": this.config?.title?.weight || "bold"
      };

      return style;
    },
    titleTextClass() {
      let classList = "";
      classList += ` pgm-dashboard-text-${this.config?.title?.size || "lg"} text-${
        this.config?.title?.horizontalAlignment || this.config?.title?.align || "left"
      }`;

      if (this.config?.title?.bold) {
        classList += " font-weight-bold";
      }
      if (this.config?.title?.italic) {
        classList += " font-italic";
      }
      if (this.config?.title?.underline) {
        classList += " text-decoration-underline";
      }

      return classList;
    },
    bodyTextStyle() {
      let style = {
        color: this.config?.body?.color || "black",
      };

      return style;
    },
    bodyTextClass() {
      let classList = "";
      classList += ` pgm-dashboard-text-${this.config?.body?.size || "md"} text-${
        this.config?.body?.horizontalAlignment || this.config?.body?.align || "left"
      }`;

      if (this.config?.body?.bold) {
        classList += " font-weight-bold";
      }
      if (this.config?.body?.italic) {
        classList += " font-italic";
      }
      if (this.config?.body?.underline) {
        classList += " text-decoration-underline";
      }

      return classList;
    },
    buttonTextStyle() {
      let style = {
        color: this.config?.button?.textColor || "black",
      };

      return style;
    },
  },
};
</script>
<style scoped>
.content-container {
  width: 100%;
  height: auto;
}

.content-image {
  object-fit: fill;
  width: 100%;
  height: auto;
}

.content-image--ratio {
  aspect-ratio: 4 / 1;
}

.overlay-text {
  position: absolute;
}

/* Body text is rendered via v-html (see the .body-text span above), so any
   nested/indented lists the admin creates in the Quill editor come out as
   FLAT sibling <li class="ql-indent-N"> elements -- Quill does not nest
   <ol>/<ul> inside <li> the way hand-written HTML would. All of the actual
   numbering (1, 2, 3 -> a, b, c -> i, ii, iii per level) and visual
   indentation is done entirely via Quill's own CSS counter-reset/
   counter-increment/::before rules, scoped under a `.ql-editor` ancestor
   class. Without that class present, the browser falls back to native flat
   ordered-list numbering, silently discarding the nesting the admin built --
   this is why the editor previously showed "1, 2, a, b, i" but the rendered
   card showed a flat "1, 2, 3, 4, 5".

   Applying the real `ql-editor` class (Quill's core CSS is already imported
   globally in App.vue) fixes this with full fidelity to whatever the admin
   saw while editing, and stays correct across future Quill upgrades instead
   of hand-duplicating ~90 lines of counter CSS here. `text-align` is NOT
   included in the overrides below: Vuetify's `.text-center`/`.text-right`
   utility classes (applied via bodyTextClass) already carry `!important` and
   higher selector specificity (`.v-application .text-center`), so they
   already win over `.ql-editor`'s plain `text-align: left` without any extra
   work (verified against node_modules/vuetify/dist/vuetify.css).

   Quality review finding (2026-07-20), HIGH: the first pass here only reset
   padding/line-height/white-space/cursor/tab-size on the .body-text span
   itself, but quill.core.css's `.ql-editor` base rule also sets box-sizing,
   height: 100%, outline, overflow-y: auto, and word-wrap -- and separately,
   an UNSCOPED `.ql-editor > * { cursor: text; }` rule that matches by class
   name alone, so it still applies to the v-html-injected <p>/<a>/<li>
   children even though the override below only touches the outer span.
   Without neutralizing that, hovering a link or list item on a clickable
   content card showed a text-select cursor instead of the card's pointer
   affordance. Neutralized fully below, including the child rule. */
.body-text.ql-editor {
  box-sizing: content-box;
  height: auto;
  outline: revert;
  overflow-y: visible;
  padding: 0;
  line-height: inherit;
  white-space: normal;
  cursor: inherit;
  tab-size: inherit;
}
/* >>> (not ::v-deep) to match this codebase's dominant deep-selector
   convention. Required here because v-html-injected children never receive
   Vue's scoped data-v-* attribute -- a plain `.body-text.ql-editor > *` rule
   would compile with the attribute required on the child and silently never
   match. */
.body-text.ql-editor >>> * {
  cursor: inherit;
}

/* Removes the grey shadow when clicking a card */
.v-card--link:before {
  background: none;
}
</style>
